import React from "react";
import { DeleteOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { Button, Popconfirm, Space } from "antd";
import PermissionGuard from "../../../../../components/auth/PermissionGuard";
import BatchActionsBar from "../../../../../components/common/BatchActionsBar";
import { useAuth } from "../../../../../hooks/useAuth";
import { useTableSelection } from "../../../../../hooks/useTableSelection";
import ScheduleDraftCreateDialog from "../../components/ScheduleDraftCreateDialog";
import ScheduleDraftListFilters from "../../components/list/ScheduleDraftListFilters";
import ScheduleDraftListTable from "../../components/list/ScheduleDraftListTable";
import useScheduleDraftListPageState from "../../hooks/useScheduleDraftListPageState";

const ScheduleDraftListPage = () => {
    const { hasPermission } = useAuth();
    const {
        data,
        isLoading,
        isFetching,
        pagination,
        createOpen,
        createDraftLoading,
        deleteDraftLoading,
        filterParams,
        quickSearchInputProps,
        summaryItems,
        openDraft,
        openCreateDialog,
        closeCreateDialog,
        handleCreateDraft,
        handleDeleteDraft,
        handleBatchDeleteDrafts,
        handleCopyDraftId,
        handleRefresh,
        handleReset,
        handleStatusChange,
        handleSourceTypeChange,
        handleTableChange,
    } = useScheduleDraftListPageState();
    const dataSource = data?.items || [];
    const {
        selectedRowKeys,
        rowSelection,
        tableRef,
        getIndexColumn,
        getRowProps,
        clearSelection,
        handleRowSelectionChange,
    } = useTableSelection({
        rowKey: "id",
        dataSource,
        pagination,
    });

    const handleSelectAllVisible = () => {
        handleRowSelectionChange(dataSource.map((item) => item.id), dataSource);
    };

    const handleDeleteAndClearSelection = async (draftId) => {
        const deleted = await handleDeleteDraft(draftId);
        if (deleted) {
            clearSelection();
        }
    };

    const handleBatchDeleteAndClearSelection = async () => {
        const result = await handleBatchDeleteDrafts(selectedRowKeys);
        if (result.successCount > 0) {
            clearSelection();
        }
    };

    const handleRow = (record, index) => {
        const rowProps = getRowProps(record, index);
        return {
            ...rowProps,
            style: { ...(rowProps.style || {}), cursor: "pointer" },
            onClick: () => openDraft(record.id),
        };
    };

    return (
        <div className="page-container">
            <div className="page-header-compact">
                <div className="page-title-compact">航班计划</div>
                <Space>
                    <PermissionGuard permission="schedule:create">
                        <Button type="primary" icon={<PlusOutlined />} onClick={openCreateDialog}>
                            新建航班计划
                        </Button>
                    </PermissionGuard>
                    <PermissionGuard permission="schedule:read">
                        <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={isFetching}>
                            刷新
                        </Button>
                    </PermissionGuard>
                </Space>
            </div>

            <ScheduleDraftListFilters
                summaryItems={summaryItems}
                quickSearchInputProps={quickSearchInputProps}
                status={filterParams.status}
                sourceType={filterParams.source_type}
                onStatusChange={handleStatusChange}
                onSourceTypeChange={handleSourceTypeChange}
                onReset={handleReset}
            />

            {selectedRowKeys.length > 0 && (
                <BatchActionsBar
                    selectedCount={selectedRowKeys.length}
                    itemName="个航班计划"
                    onSelectAll={handleSelectAllVisible}
                    onClearSelection={clearSelection}
                    actions={[
                        {
                            key: "delete",
                            label: "批量删除",
                            icon: <DeleteOutlined />,
                            danger: true,
                            loading: deleteDraftLoading,
                            render: () => (
                                <PermissionGuard permission="schedule:delete">
                                    <Popconfirm
                                        title="批量删除"
                                        description={`确认删除选中的 ${selectedRowKeys.length} 个航班计划吗？`}
                                        onConfirm={handleBatchDeleteAndClearSelection}
                                        okText="确认"
                                        cancelText="取消"
                                    >
                                        <Button size="small" type="primary" ghost danger icon={<DeleteOutlined />} loading={deleteDraftLoading} style={{ fontSize: "12px" }}>
                                            批量删除 ({selectedRowKeys.length})
                                        </Button>
                                    </Popconfirm>
                                </PermissionGuard>
                            ),
                        },
                    ]}
                />
            )}

            <ScheduleDraftListTable
                tableRef={tableRef}
                rowSelection={hasPermission("schedule:delete") ? rowSelection : null}
                indexColumn={getIndexColumn("序号", 60)}
                dataSource={dataSource}
                total={data?.total || 0}
                loading={isLoading}
                pagination={pagination}
                onTableChange={handleTableChange}
                onRow={handleRow}
                onOpenDraft={openDraft}
                onCopyDraftId={handleCopyDraftId}
                onDeleteDraft={handleDeleteAndClearSelection}
                deleteDraftLoading={deleteDraftLoading}
            />

            <ScheduleDraftCreateDialog open={createOpen} confirmLoading={createDraftLoading} onCancel={closeCreateDialog} onSubmit={handleCreateDraft} />
        </div>
    );
};

export default ScheduleDraftListPage;
